import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  ApiEnvelope,
  ChapterRecord,
  ClassRecord,
  SubjectRecord,
} from "@/lib/api-types";

/**
 * Curriculum endpoints return arrays directly under `data`:
 *   GET /curriculum/classes                  -> data: ClassRecord[]
 *   GET /curriculum/classes/:id/subjects     -> data: SubjectRecord[]
 *   GET /curriculum/subjects/:id/chapters    -> data: ChapterRecord[]
 *
 * Cached 5 min -- curriculum changes are infrequent.
 */

export function useClasses() {
  return useQuery({
    queryKey: qk.curriculum.classes(),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ClassRecord[]>>("/curriculum/classes");
      return unwrap(res.data) ?? [];
    },
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useSubjects(classId: string | undefined) {
  return useQuery({
    queryKey: qk.curriculum.subjects(classId ?? ""),
    enabled: !!classId,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<SubjectRecord[]>>(
        `/curriculum/classes/${encodeURIComponent(classId!)}/subjects`,
      );
      return unwrap(res.data) ?? [];
    },
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useChapters(subjectId: string | undefined, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.curriculum.chapters(subjectId ?? ""),
    enabled: !!subjectId && (opts?.enabled ?? true),
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ChapterRecord[]>>(
        `/curriculum/subjects/${encodeURIComponent(subjectId!)}/chapters`,
      );
      const list = unwrap(res.data) ?? [];
      return [...list].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    },
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}
